import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _isObjectLike from 'lodash/isObjectLike';
import { DATA_ENHANCED_ATTRIBUTE, DATA_ENHANCED_ATTRIBUTE_TRUE, MUTATION_OBSERVER_TAG } from './BotpressProvider.constants';
import { EVENT_ACTIONS, EVENT_CATEGORIES } from '../../../analytics/components/Event';
import { BOTPRESS_DEFAULT_CONFIG } from '../../constants';
import { processGoogleAnalyticsEvent } from '../../services/eventProcessors';

/**
 * Merges two objects, with values from overrideObj taking precedence over defaultObj.
 * Only non-null/undefined values from overrideObj are merged.
 * For nested objects, merging is done recursively.
 *
 * @param {Object} defaultObj - The default object to merge into
 * @param {Object} overrideObj - The object containing override values
 * @returns {Object} A new merged object
 *
 * @example
 * const default = { a: 1, b: { c: 2 } };
 * const override = { b: { d: 3 } };
 * mergeObjectIfNotEmpty(default, override);
 * // Returns: { a: 1, b: { c: 2, d: 3 } }
 *
 * @example
 * const default = { a: undefined, b: 1 };
 * const override = { a: null, b: null };
 * mergeObjectIfNotEmpty(default, override);
 * // Returns: { a: undefined, b: 1 }
 */
export function mergeObjectIfNotEmpty(defaultObj, overrideObj) {
  if (_isNil(overrideObj) || _isObjectLike(overrideObj) && _isEmpty(overrideObj)) {
    return defaultObj;
  }
  var result = _cloneDeep(defaultObj);
  Object.entries(overrideObj).forEach(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      value = _ref2[1];
    if (!_isNil(value)) {
      // Only recursively merge if both values are objects
      if (_isObjectLike(value) && _isObjectLike(defaultObj[key])) {
        result[key] = mergeObjectIfNotEmpty(defaultObj[key], value);
      } else {
        result[key] = value;
      }
    }
  });
  return result;
}
export function createConfig() {
  var overrides = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return mergeObjectIfNotEmpty(BOTPRESS_DEFAULT_CONFIG, overrides);
}
function handleLinkClick(link, navigationHandler) {
  var href = link.href;
  var linkUrl = new URL(href);
  var currentDomain = window.location.hostname;
  var linkDomain = linkUrl.hostname;
  var isExternalLink = currentDomain !== linkDomain;
  processGoogleAnalyticsEvent({
    payload: {
      eventData: {
        category: EVENT_CATEGORIES.NAVIGATION_EVENT_CATEGORY,
        action: EVENT_ACTIONS.WEBCHAT_LINK_CLICK_EVENT_ACTION,
        label: linkDomain,
        value: href
      }
    }
  });
  navigationHandler(isExternalLink, href);
}
export function enhanceLink(link, navigationHandler) {
  if (link.getAttribute(DATA_ENHANCED_ATTRIBUTE)) {
    return;
  }
  var enhancedLink = link.cloneNode(true);
  enhancedLink.setAttribute(DATA_ENHANCED_ATTRIBUTE, DATA_ENHANCED_ATTRIBUTE_TRUE);
  enhancedLink.onclick = function (e) {
    e.preventDefault();
    handleLinkClick(link, navigationHandler);
  };
  if (link.parentNode) {
    link.parentNode.replaceChild(enhancedLink, link);
  }
}
export function createLinkObserver(navigationHandler) {
  return new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            var links = _toConsumableArray(node.getElementsByTagName(MUTATION_OBSERVER_TAG));
            links.forEach(function (link) {
              return enhanceLink(link, navigationHandler);
            });
          }
        });
      }
    });
  });
}